// MASTER-PROJECT-WIZARD-INTEGRATION-001 (task section 26): the shared
// error-detail normalizer for every canonical Master-project surface
// (Wizard routing, ProjectView, MasterContentDetail) — maps a controlled
// backend `{code, message}` detail to a stable, localized i18n key. The
// raw backend `code`/`status` are kept on the returned object for
// developer-console debugging only; the UI must always render the
// mapped message, never the raw backend `detail` object (which would
// otherwise print as `[object Object]`).


const VALIDATION_FIELD_KEYS = {
  title: 'masterProject.detail.title',
  primary_hook: 'masterProject.detail.primaryHook',
  angle: 'masterProject.detail.angle',
  audience: 'masterProject.detail.audience',
  objective: 'masterProject.detail.objective',
  thesis: 'masterProject.detail.thesis',
  body_markdown: 'masterProject.detail.body',
  cta: 'masterProject.detail.cta',
};

function resolveFastApiValidationDetail(detail, t) {
  if (!Array.isArray(detail) || detail.length === 0) return null;
  const issue = detail[0] || {};
  const loc = Array.isArray(issue.loc) ? issue.loc : [];
  const field = loc[loc.length - 1];
  const fieldKey = VALIDATION_FIELD_KEYS[field];
  const fieldLabel = fieldKey ? t(fieldKey) : t('masterProject.detail.validation.fieldGeneric');
  const min = issue?.ctx?.min_length;
  const max = issue?.ctx?.max_length;

  if (issue.type === 'string_too_short' && Number.isFinite(min)) {
    return t('masterProject.detail.validation.fieldTooShort', { field: fieldLabel, min });
  }
  if (issue.type === 'string_too_long' && Number.isFinite(max)) {
    return t('masterProject.detail.validation.fieldTooLong', { field: fieldLabel, max });
  }
  if (issue.type === 'missing') {
    return t('masterProject.detail.validation.fieldRequired', { field: fieldLabel });
  }
  return t('masterProject.detail.validation.serverRejected', { field: fieldLabel });
}

const ERROR_CODE_KEYS = {
  master_project_required: 'masterProject.errors.masterProjectRequired',
  social_project_required: 'masterProject.errors.socialProjectRequired',
  pro_feature_required: 'masterProject.errors.locked',
  master_project_generation_in_progress: 'masterProject.errors.generationInProgress',
  master_project_generation_inconsistent: 'masterProject.errors.generationInconsistent',
  master_project_completion_outcome_unknown: 'masterProject.errors.completionOutcomeUnknown',
  master_project_content_persistence_failed: 'masterProject.errors.persistenceFailed',
  master_project_completion_failed: 'masterProject.errors.completionFailed',
  master_project_generation_failed: 'masterProject.errors.generationFailed',
  canonical_master_integrity_error: 'masterProject.errors.integrityError',
  canonical_master_requires_dedicated_editor: 'masterProject.errors.requiresDedicatedEditor',
  canonical_master_delete_not_supported: 'masterProject.errors.deleteNotSupported',
  canonical_master_regeneration_not_supported: 'masterProject.errors.regenerationNotSupported',
  canonical_master_derivation_not_available: 'masterProject.errors.derivationNotAvailable',
  canonical_master_batch_not_distinct: 'masterProject.errors.batchNotDistinct',
  canonical_master_revision_conflict: 'masterProject.errors.revisionConflict',
  // MASTER-PROJECT-WIZARD-INTEGRATION-001B: the batch-edit claim is held
  // by another request (or its lease renewal was lost) — never falls
  // back to the generic error, never shows the raw backend detail.
  canonical_master_edit_in_progress: 'masterProject.errors.editInProgress',
  // HOTFIX-MASTER-PLAN-RETRY-001: the underlying Pro Master AI
  // orchestration (`pro_master_generation_service`) can surface these
  // `pro_master_*` codes directly to the Wizard — `generate_validated_
  // batch` errors propagate unwrapped through master_project_content_
  // service. Never fall back to the generic error for any of these.
  pro_master_plan_invalid: 'masterProject.errors.planInvalid',
  pro_master_generation_invalid: 'masterProject.errors.generationInvalid',
  pro_master_quality_failed: 'masterProject.errors.qualityFailed',
  pro_master_batch_not_distinct: 'masterProject.errors.generationBatchNotDistinct',
  pro_master_provider_error: 'masterProject.errors.providerError',
  pro_master_context_insufficient: 'masterProject.errors.contextInsufficient',
};

// Returns { key, code, status } — the component calls t(result.key).
// Never returns the raw detail object; a completely unrecognized
// error/response shape still resolves to a safe generic key.
export function mapMasterProjectError(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const code = detail && typeof detail === 'object' ? detail.code : undefined;

  if (code && ERROR_CODE_KEYS[code]) {
    return { key: ERROR_CODE_KEYS[code], code, status };
  }
  if (status === 404) {
    return { key: 'masterProject.errors.notFound', code: 'not_found', status };
  }
  if (typeof detail === 'string' && detail.trim()) {
    // A plain-string detail (e.g. a Pydantic/validation message from the
    // dedicated edit endpoint) is safe to surface directly — it never
    // contains generated prose, only a description of what the user
    // themselves supplied incorrectly.
    return { key: null, message: detail, code: code || 'validation_error', status };
  }
  return { key: 'masterProject.errors.generic', code: code || 'unknown', status };
}

// The single safe-rendering helper every component should use: resolves
// mapMasterProjectError's result to a plain, translated string, never
// "[object Object]" and never a raw backend payload.
export function resolveMasterProjectErrorMessage(error, t) {
  const detail = error?.response?.data?.detail;
  const validationMessage = resolveFastApiValidationDetail(detail, t);
  if (validationMessage) return validationMessage;

  const mapped = mapMasterProjectError(error);
  if (mapped.key) return t(mapped.key);
  return mapped.message || t('masterProject.errors.generic');
}
